import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_SPARKLES = 56;
const MOVE_SPARKLE_MS = 22;
const MIN_MOVE_PX = 5;

function useSparkleCursorEnabled() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => {
      setEnabled(!reduced.matches && fine.matches);
    };
    sync();
    reduced.addEventListener('change', sync);
    fine.addEventListener('change', sync);
    return () => {
      reduced.removeEventListener('change', sync);
      fine.removeEventListener('change', sync);
    };
  }, []);

  return enabled;
}

export default function SparkleCursor() {
  const location = useLocation();
  const enabled = useSparkleCursorEnabled();
  const [pos, setPos] = useState({ x: -100, y: -100 });
  const [sparkles, setSparkles] = useState([]);
  const idRef = useRef(0);
  const lastSparkleRef = useRef(0);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(0);

  const active =
    enabled && location.pathname !== '/admin' && location.pathname !== '/admin-login';

  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add('sparkle-cursor-hide-system');
    return () => {
      document.body.classList.remove('sparkle-cursor-hide-system');
    };
  }, [active]);

  const emitSparkles = useCallback((x, y) => {
    const count = 1 + (Math.random() > 0.55 ? 1 : 0);
    const batch = [];
    for (let i = 0; i < count; i += 1) {
      batch.push({
        id: idRef.current,
        x,
        y,
        tx: (Math.random() - 0.5) * 56,
        ty: (Math.random() - 0.5) * 56,
        sz: 3 + Math.random() * 7,
        hue: 200 + Math.random() * 45,
      });
      idRef.current += 1;
    }
    setSparkles((prev) => [...prev, ...batch].slice(-MAX_SPARKLES));
  }, []);

  const removeSparkle = useCallback((id) => {
    setSparkles((prev) => prev.filter((s) => s.id !== id));
  }, []);

  useEffect(() => {
    if (!active) return undefined;

    const onMove = (e) => {
      const x = e.clientX;
      const y = e.clientY;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setPos({ x, y });
        const now = performance.now();
        const lp = lastPosRef.current;
        const dist = Math.hypot(x - lp.x, y - lp.y);
        lastPosRef.current = { x, y };
        if (dist >= MIN_MOVE_PX && now - lastSparkleRef.current >= MOVE_SPARKLE_MS) {
          lastSparkleRef.current = now;
          emitSparkles(x, y);
        }
      });
    };

    const onLeave = () => {
      setPos({ x: -100, y: -100 });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
    };
  }, [active, emitSparkles]);

  if (!active) return null;

  return (
    <div className="sparkle-cursor-layer" aria-hidden="true">
      <div
        className="sparkle-cursor-dot"
        style={{ left: pos.x, top: pos.y }}
      />
      {sparkles.map((s) => (
        <div
          key={s.id}
          className="sparkle-cursor-particle"
          style={{
            left: s.x,
            top: s.y,
            '--tx': `${s.tx}px`,
            '--ty': `${s.ty}px`,
            '--sz': `${s.sz}px`,
            '--hue': String(s.hue),
          }}
          onAnimationEnd={() => removeSparkle(s.id)}
        />
      ))}
    </div>
  );
}
